import { ReactNode, useId, useLayoutEffect, useRef, useState } from "react";
import cx from "classnames";

import TextButton from "client/common/components/buttons/TextButton";

import styles from "../../styles/ReadMoreText.module.less";

type ReadMoreTextProps = {
  children: ReactNode;
  className?: string;
};

const ReadMoreText = ({ children, className }: ReadMoreTextProps) => {
  const textId = useId();
  const textRef = useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [canToggle, setCanToggle] = useState(false);

  useLayoutEffect(() => {
    setIsExpanded(false);
    setCanToggle(false);
  }, [children]);

  useLayoutEffect(() => {
    const textEl = textRef.current;
    if (!textEl || isExpanded) {
      return;
    }

    // Ignore sub-pixel layout differences when comparing clamped vs full height.
    const overflowThresholdPx = 4;

    setCanToggle(textEl.scrollHeight - textEl.clientHeight > overflowThresholdPx);
  }, [children, isExpanded]);

  const ReadButton = ({ children: label }: { children: ReactNode }) => (
    <TextButton
      type="button"
      className={styles.toggle}
      aria-hidden={true}
      onClick={() => setIsExpanded((expanded) => !expanded)}
    >
      {label}
    </TextButton>
  );

  return (
    <div className={cx(styles.container, className)}>
      {isExpanded ? (
        <span id={textId} className={styles.textExpanded}>
          {children}
          <>
            {" "}
            <ReadButton>Read less</ReadButton>
          </>
        </span>
      ) : (
        <>
          <div ref={textRef} id={textId} className={cx(styles.text, styles.clamped)}>
            {children}
          </div>
          {canToggle && <ReadButton>Read more</ReadButton>}
        </>
      )}
    </div>
  );
};

export default ReadMoreText;
